'use client';

import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslations } from 'next-intl';
import { twMerge } from 'tailwind-merge';
import CopyIcon from '@/ui/components/shared/icons/copy-icon';
import Button from '../Button';
import { ButtonVariants } from '../Button/types/ButtonVariants';

interface CopyVoucherButtonProps {
  code: string;
  variant?: ButtonVariants;
  className?: string;
}

const COPIED_RESET_MS = 1500;

/**
 * Voucher CTA: a normal Button that copies the code to the clipboard and briefly flips its label to
 * "copied". It renders with the CTA's own `variant` so it looks like the campaign's other buttons
 * (e.g. a `secondary` CTA gives the white pill from the Figma) rather than a bespoke style.
 */
export function CopyVoucherButton({ code, variant, className }: Readonly<CopyVoucherButtonProps>) {
  const t = useTranslations('Button');
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), COPIED_RESET_MS);
  };

  return (
    <CopyToClipboard text={code} onCopy={handleCopy}>
      <Button
        variant={variant}
        aria-label={`${t('copyCode')} ${code}`}
        className={twMerge('inline-flex w-fit items-center justify-center gap-2', className)}
      >
        <CopyIcon className="h-5 w-5 shrink-0" />
        {isCopied ? t('copied') : t('copyCode')}
      </Button>
    </CopyToClipboard>
  );
}
